"use client";

import React from "react";
import styles from "./overview-card.module.scss";
import { HealthResponse } from "@/types/health.types";

interface OverviewCardProps {
  healthData: HealthResponse;
}

const OverviewCard: React.FC<OverviewCardProps> = ({ healthData }) => {
  // Check if rollup is truly healthy (all events and signers must be healthy)
  const getRollupStatus = () => {
    if (!healthData.rollup) return false;

    const allEventsHealthy = [
      ...healthData.rollup.contracts.rollup.events,
      ...healthData.rollup.contracts.multiSig.events
    ].every(event => event.healthy);

    const allSignersHealthy = healthData.rollup.signers.every(signer => signer.healthy);

    return allEventsHealthy && allSignersHealthy;
  };



  const getTotalServices = () => {
    return healthData.services.healthy.length + healthData.services.unhealthy.length;
  };

  const getHealthyServicesCount = () => {
    return healthData.services.healthy.length;
  };

  const formatBadge = (healthy: number, total: number) => {
    return { healthy, total, isHealthy: healthy === total };
  };

  const getHeartbeatsMissing = () => {
    if (!healthData.processes) return 0;
    const totalServices = healthData.services.healthy.length + healthData.services.unhealthy.length;
    const onlineProcesses = healthData.processes.onlineProcesses;
    return onlineProcesses - totalServices;
  };

  const getBlockchainCounts = () => {
    const items: { healthy: boolean }[] = [];

    // Latest Block
    items.push({ healthy: healthData.latestBlock.healthy });

    // Network Ping
    items.push({ healthy: healthData.ping.healthy });

    // Rollup Contract Events (individual events)
    if (healthData.rollup) {
      // Add rollup contract events if they exist
      if (healthData.rollup.contracts.rollup?.events) {
        healthData.rollup.contracts.rollup.events.forEach(event => {
          items.push({ healthy: event.healthy });
        });
      }

      // Add multiSig contract events if they exist
      if (healthData.rollup.contracts.multiSig?.events) {
        healthData.rollup.contracts.multiSig.events.forEach(event => {
          items.push({ healthy: event.healthy });
        });
      }

      // Rollup Signers (individual signers) - only if they exist
      if (healthData.rollup.signers && healthData.rollup.signers.length > 0) {
        healthData.rollup.signers.forEach(signer => {
          items.push({ healthy: signer.healthy });
        });
      }
    }

    const healthyCount = items.filter(item => item.healthy).length;
    const totalCount = items.length;

    return { healthy: healthyCount, total: totalCount };
  };

  const getRollupSignerCounts = () => {
    if (!healthData.rollup || !healthData.rollup.signers || healthData.rollup.signers.length === 0) {
      return { healthy: 0, total: 0 };
    }

    const healthyCount = healthData.rollup.signers.filter(signer => signer.healthy).length;
    const totalCount = healthData.rollup.signers.length;

    return { healthy: healthyCount, total: totalCount };
  };

  const getInfraCounts = () => {
    const items: { healthy: boolean }[] = [];

    // Delivery service - 2 balances (blessnet + parent chain)
    if (healthData.delivery?.available) {
      items.push({ healthy: healthData.delivery.blessnetHealthy });
      items.push({ healthy: healthData.delivery.parentChainHealthy });
    }

    // Token dispenser - 1 balance
    if (healthData.dispenser?.available) {
      items.push({ healthy: healthData.dispenser.healthy });
    }

    // Sprinkler service - 1 balance
    if (healthData.sprinkler?.available) {
      items.push({ healthy: healthData.sprinkler.healthy });
    }

    // Bless router - 1 balance
    if (healthData.blessRouter?.available) {
      items.push({ healthy: healthData.blessRouter.healthy });
    }

    const healthyCount = items.filter(item => item.healthy).length;
    const totalCount = items.length;

    return { healthy: healthyCount, total: totalCount };
  };

  const getGatewayCounts = () => {
    const items: { healthy: boolean }[] = [];

    // Token gateway balances - each gateway has 2 balances (ETH + BLESS)
    if (healthData.tokenGateways?.available) {
      healthData.tokenGateways.gateways.forEach(gateway => {
        items.push({ healthy: gateway.ethHealthy });
        items.push({ healthy: gateway.blessHealthy });
      });
    }

    // Transaction gateway balances - 2 balances (ETH + BLESS)
    if (healthData.transactionGateway?.available) {
      items.push({ healthy: healthData.transactionGateway.ethHealthy });
      items.push({ healthy: healthData.transactionGateway.blessHealthy });
    }

    const healthyCount = items.filter(item => item.healthy).length;
    const totalCount = items.length;

    return { healthy: healthyCount, total: totalCount };
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

    return (
    <div className={styles.card}>
      <div className={styles.content}>
        <div className={styles.compactGrid}>
          <div className={styles.metricCompact}>
            <span className={styles.label}>Services</span>
            <div
              className={`${styles.badge} ${formatBadge(getHealthyServicesCount(), getTotalServices()).isHealthy ? styles.badgeHealthy : styles.badgeUnhealthy}`}
              onClick={() => scrollToSection('services-status')}
            >
              {getHealthyServicesCount()}/{getTotalServices()}
            </div>
          </div>

          {/* Only show Heartbeats Missing if we have processes data */}
          {healthData.processes && (
            <div className={styles.metricCompact}>
              <span className={styles.label}>Heartbeats Missing</span>
              <div className={`${styles.badge} ${getHeartbeatsMissing() === 0 ? styles.badgeHealthy : styles.badgeUnhealthy}`}>
                {getHeartbeatsMissing()}
              </div>
            </div>
          )}

          {/* Only show Token Gateways if we have gateway data */}
          {(healthData.tokenGateways?.available || healthData.transactionGateway?.available) && (
            <div className={styles.metricCompact}>
              <span className={styles.label}>Token Gateways</span>
              <div
                className={`${styles.badge} ${formatBadge(getGatewayCounts().healthy, getGatewayCounts().total).isHealthy ? styles.badgeHealthy : styles.badgeUnhealthy}`}
                onClick={() => scrollToSection('gateways-status')}
              >
                {getGatewayCounts().healthy}/{getGatewayCounts().total}
              </div>
            </div>
          )}

          {/* Only show Rollup Signers if we have signers */}
          {healthData.rollup?.signers && healthData.rollup.signers.length > 0 && (
            <div className={styles.metricCompact}>
              <span className={styles.label}>Rollup Signers</span>
              <div
                className={`${styles.badge} ${formatBadge(getRollupSignerCounts().healthy, getRollupSignerCounts().total).isHealthy ? styles.badgeHealthy : styles.badgeUnhealthy}`}
                onClick={() => scrollToSection('blockchain-status')}
              >
                {getRollupSignerCounts().healthy}/{getRollupSignerCounts().total}
              </div>
            </div>
          )}

                    <div className={styles.metricCompact}>
            <span className={styles.label}>Blockchain</span>
            <div
              className={`${styles.badge} ${formatBadge(getBlockchainCounts().healthy, getBlockchainCounts().total).isHealthy ? styles.badgeHealthy : styles.badgeUnhealthy}`}
              onClick={() => scrollToSection('blockchain-status')}
            >
              {getBlockchainCounts().healthy}/{getBlockchainCounts().total}
            </div>
          </div>

          {/* Only show Infra Balances if we have infrastructure data */}
          {(healthData.delivery?.available || healthData.dispenser?.available || healthData.sprinkler?.available || healthData.blessRouter?.available) && (
            <div className={styles.metricCompact}>
              <span className={styles.label}>Infra Balances</span>
              <div
                className={`${styles.badge} ${formatBadge(getInfraCounts().healthy, getInfraCounts().total).isHealthy ? styles.badgeHealthy : styles.badgeUnhealthy}`}
                onClick={() => scrollToSection('infrastructure-status')}
              >
                {getInfraCounts().healthy}/{getInfraCounts().total}
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <span className={styles.chainInfo}>
            {healthData.ping.chainId === 11145513 ? "Blessnet Sepolia" : `Chain ${healthData.ping.chainId}`}
          </span>
          <span className={styles.lastUpdated}>
            Updated {new Date().toLocaleTimeString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OverviewCard;
